import { Button, Modal } from "antd";
import { FC, PropsWithChildren, useCallback, useMemo } from "react";

import { usePartnership } from "@app/pages/partnership/model/partnership-provider";
import { useNotifier, useSchool } from "@app/shared/lib/hooks";
import { Loc, MarkdownText } from "@app/shared/ui";

type Props = {
  onCancel?: () => void;
};

export const AgreementBlocker: FC<PropsWithChildren<Props>> = ({
  onCancel,
  children,
}) => {
  const [nofity] = useNotifier();
  const { currentMember, acceptAgreement, loading } = usePartnership();

  const { partnership } = useSchool();
  const agreementOpen = useMemo(
    () => partnership?.agreementRequired && !currentMember?.acceptedDate,
    [currentMember?.acceptedDate, partnership?.agreementRequired],
  );

  const handleAcceptAgreement = useCallback(async () => {
    const result = await acceptAgreement();
    if (!result.success) {
      onCancel?.();
      nofity(result);
    }
  }, [acceptAgreement, nofity, onCancel]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (!agreementOpen) return <>{children}</>;

  return (
    <Modal
      open={agreementOpen}
      title={
        <Loc word="Partnership.agreement" default="Partnership agreement" />
      }
      destroyOnClose
      keyboard={false}
      maskClosable={false}
      closable={false}
      width={400}
      footer={null}
    >
      {partnership?.agreementText && (
        <MarkdownText text={partnership.agreementText} />
      )}

      <footer className="flex justify-end gap-10">
        <Button onClick={onCancel} disabled={loading.accepting}>
          <Loc word="Global.cancel" default="Cancel" />
        </Button>
        <Button
          type="primary"
          onClick={handleAcceptAgreement}
          loading={loading.accepting}
        >
          <Loc word="Global.accept" default="Accept" />
        </Button>
      </footer>
    </Modal>
  );
};
